import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { cn } from "@volca/ui/lib/utils";
import { GUTTER, SIGN, TINT } from "./diff-marks";
import type { DiffBlock } from "./rules-diff";
import { HIT, HIT_ACTIVE, componentsFor } from "./rules-markup";
/** A fenced code block, which means its indentation on purpose. */
const FENCE = /^\s*(```|~~~)/;

/**
 * The passage's lines, with accidental indentation taken off.
 *
 * A passage is rendered on its own, so the list it belonged to is in another
 * block and its indented continuation lines read as a code block to the
 * markdown parser. Those were dropped outright, which deleted the content of
 * every nested bullet from the page. Taking the common indent off restores the
 * words. A fenced block keeps whatever it has, because that indentation is
 * something the author wrote.
 */
const readable = (lines: string[]) => {
  const text = lines.join("\n");
  const filled = lines.filter((line) => line.trim());
  if (!filled.length || filled.some((line) => FENCE.test(line))) return text;
  const indent = Math.min(
    ...filled.map((line) => line.length - line.trimStart().length),
  );
  return indent >= 4
    ? lines.map((line) => line.slice(indent)).join("\n")
    : text;
};

export const RulesPassage = ({
  block,
  active,
  at,
  needle,
  hit,
}: {
  block: DiffBlock;
  active: boolean;
  /** Index in the document, which is also this passage's search id. */
  at: number;
  /** Lowercased search text, empty when nothing is being searched. */
  needle: string;
  /** The passage the reader has stepped to. */
  hit: boolean;
}) => (
  <div
    // The passage says which edit it is. Cycling finds it by asking the
    // document, which used to mean a Map of nodes kept in a ref — and a Map
    // holds whatever the last render put in it, so after the assistant rewrote
    // the plan it still pointed at passages that were no longer there.
    data-change={block.change ?? undefined}
    data-passage={at}
    className={cn(
      "grid grid-cols-[1.5rem_1fr]",
      TINT[block.kind],
      // Where cycling just sent the reader. Neutral ink on purpose: a green
      // ring round a red passage argues with the colour that carries meaning.
      // Inset on the left edge only: a full ring drew a heavy rule between a
      // removed row and the added row directly beneath it.
      { "shadow-[inset_2px_0_0_0_var(--line)]": active },
    )}
  >
    <span
      aria-hidden
      className={cn(
        "pt-1 text-center text-xs leading-5 font-semibold select-none",
        GUTTER[block.kind],
      )}
    >
      {SIGN[block.kind]}
    </span>
    {/* The dense typeset: a diff row is a line of a document, and a
        paragraph's full margin between adjacent rows opened gaps that read as
        missing content. */}
    <div className="typeset typeset-dense min-w-0 px-2 py-1">
      {block.kind !== "same" && (
        <span className="sr-only">
          {block.kind === "removed"
            ? "Removed:"
            : block.kind === "changed"
              ? "Changed:"
              : "Added:"}
        </span>
      )}
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={componentsFor(block.kind, needle, hit ? HIT_ACTIVE : HIT)}
      >
        {readable(block.lines)}
      </ReactMarkdown>
    </div>
  </div>
);
